import React, { useCallback, useMemo, useState } from 'react';

import { activeSearchHitColor, inactiveSearchHitColor, PDFCustomDataKey } from '@aline/common';
import { CircularProgress, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { format, isAfter, isValid, parseISO } from 'date-fns';
import { CircleAlert, Lock, Pencil, TriangleAlert } from 'lucide-react';
import { Editor, Node, Path } from 'slate';
import {
  AlineTooltip,
  getFileReferenceQuad,
  pdfTronCore,
  useClearSearchHighlights,
  useMaybeDocumentViewer,
} from 'src/components';
import { ExtractionQuestionCitation } from 'src/components/ai/report/components/ExtractionQuestionCitation';
import { AiReasoningSection } from 'src/components/ai/shared/components/AiReasoningSection';
import { counterResetStyles } from 'src/components/ai/shared/components/Citation';
import { getIsJobLocked, getLockedByTooltip } from 'src/components/reports';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { OptionItemChip } from 'src/editor/toolbars/right-panel/ai';
import type { ExtractionJobReview, QuestionCardQuestion } from 'src/editor/toolbars/right-panel/ai';
import { ClientTransforms } from 'src/editor/transforms';
import { Apollo } from 'src/gql/generated';
import { Flag, useDispatchNotification } from 'src/helpers';
import { ReactEditor, useMaybeEditor } from 'src/layout-engine';
import { Sentry } from 'src/sentry';
import type { ReactSetStateTuple } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import { EditAiAnswerForm } from './EditAiAnswer';
import { getHasAiReasoning } from './extraction-job-utils';
import type { DocumentWithAiQuestionIdsQueueing } from '../../../document-summary/DocumentSummaryDrawer';
import { getCMLocationFromReference } from '../../shared/getCMLocationFromReference';

export const SearchResultSchema = z.object({
  ambientStr: z.string(),
  pageNum: z.number(),
  // See https://community.apryse.com/t/what-is-the-quads-property-in-core-documentviewer-searchresult/8229/5 for discussion of this object, which isn't properly documented by Apryse.
  quads: z.array(z.any()).default([]),
  resultCode: z.number(),
  resultStr: z.string(),
  resultStrEnd: z.number(),
  resultStrStart: z.number(),
});

export const useExtractionJobStyles = makeStyles()(theme => ({
  actionButtonsContainer: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    marginLeft: 'auto',
  },
  answerBox: {
    backgroundColor: theme.appColors.background,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  answerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  answerHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  answerText: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  answerValue: {
    color: theme.palette.text.primary,
    fontSize: '16px',
    width: '100%',
  },
  blueText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  carousel: {
    display: 'flex',
  },
  carouselButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
  },
  clickableAnswer: {
    '&:hover': {
      backgroundColor: theme.appColors.darkBackground,
    },
    cursor: 'pointer',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 1),
  },
  job: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  lockedAnswerIcon: {
    color: theme.palette.text.secondary,
    display: 'flex',
  },
  nextBackButtons: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  reasoningSection: {
    paddingTop: theme.spacing(1),
  },
  referencesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    /**
     * Scopes the citation counter to this answer's reference list, so it numbers 1, 2, 3 and
     * restarts on the next question. Without a reset on an ancestor of the citations, each one
     * implicitly creates the counter on its own pseudo-element and every chip renders "1".
     */
    ...counterResetStyles,
  },
  smallIcon: {
    height: 16,
    width: 16,
  },
  topAnswerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
}));

interface ExtractionJobProps {
  document: DocumentWithAiQuestionIdsQueueing;
  question: QuestionCardQuestion;
}

export function ExtractionJob({ document, question }: ExtractionJobProps) {
  const theme = useTheme();
  const { classes } = useExtractionJobStyles();
  const isCoreAiRunning = getIsCoreAiRunning(document);
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const isExtractionJobRunning = getIsExtractionQuestionRunning(document, question);
  const isDocumentChangedSinceExtraction = getIsExtractionJobStale(
    document,
    question.extractionJob
  );
  const isCoreQuestion = question.variant !== Apollo.ExtractionQuestionVariant.Custom;
  const isCoreAiRunningForQuestion =
    Flag.AUTO_RUN_CORE_AI_STANDARD_PROPERTIES && isCoreQuestion && isCoreAiRunning;
  const isAiMode = question.mode === Apollo.ExtractionQuestionMode.Ai;

  if (isExtractionJobRunning || isCoreAiRunningForQuestion)
    return (
      <div className={classes.job}>
        <div className={classes.answerBox}>
          <div className={classes.container}>
            <CircularProgress size={18} />
            <Typography align="center" className={classes.blueText}>
              Aline AI thinking...
            </Typography>
          </div>
        </div>
      </div>
    );

  if (isDocumentChangedSinceExtraction)
    return (
      <div className={classes.job}>
        <div className={classes.answerBox}>
          <div className={classes.container}>
            <TriangleAlert color={theme.palette.warning.main} />
            <Typography align="center">
              Document content changed invalidating prior answer. Rerun.
            </Typography>
          </div>
        </div>
      </div>
    );

  if (question.extractionJob?.failedAt) {
    return (
      <div className={classes.job}>
        <div className={classes.answerBox}>
          <div className={classes.container}>
            <CircleAlert color={theme.palette.error.main} />
            <Typography align="center">Extraction Failed</Typography>
          </div>
        </div>
      </div>
    );
  }

  if (!question.extractionJob && isAiMode) return null;

  return (
    <div
      className={classes.job}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <AiAnswer
        documentId={document.id}
        extractionJob={question.extractionJob}
        mouseOver={mouseOver}
        question={question}
      />
    </div>
  );
}

export type SelectedAlineReference = Pick<
  Apollo.AlineLocation,
  'locationPath' | 'referenceText' | '__typename'
>;
export type SelectedFileReference = Pick<
  Apollo.FileLocation,
  'pageNum' | 'rect' | 'referenceText' | '__typename'
>;
export type SelectedReference = SelectedAlineReference | SelectedFileReference;

interface AiAnswerExtractionJob extends Pick<
  Apollo.ExtractionJob,
  'id' | 'answer' | 'lockedAt' | 'locations' | 'reasoning' | '__typename'
> {
  lockedBy: Pick<Apollo.User, '__typename' | 'displayName' | 'id'> | null;
  review: ExtractionJobReview | null;
  selectedOptions: Pick<Apollo.QuestionOption, 'id' | '__typename' | 'label'>[];
}

interface AiAnswerProps {
  documentId: string;
  extractionJob: AiAnswerExtractionJob | null;
  mouseOver: boolean;
  question: Pick<QuestionCardQuestion, 'mode' | 'type' | 'id' | '__typename'>;
}

export function sortExtractionLocations<T extends SelectedReference>(
  locations: readonly T[] | null | undefined
): T[] {
  if (!locations) return [];
  return [...locations].sort((a, b) => {
    if (a.__typename === 'FileLocation' && b.__typename === 'FileLocation') {
      const [aLeft = 0, aTop = 0] = a.rect;
      const [bLeft = 0, bTop = 0] = b.rect;
      const yDiff = aTop - bTop;
      const xDiff = aLeft - bLeft;
      const pageDiff = a.pageNum - b.pageNum;

      return pageDiff !== 0 ? pageDiff : yDiff !== 0 ? yDiff : xDiff;
    }

    if (a.__typename === 'AlineLocation' && b.__typename === 'AlineLocation')
      return Path.compare(a.locationPath, b.locationPath);

    return 0;
  });
}

function AiAnswer({ documentId, extractionJob, mouseOver, question }: AiAnswerProps) {
  const { classes } = useExtractionJobStyles();
  const [editing, setEditing] = useState<boolean>(false);
  const sortedReferences = useMemo(
    () => sortExtractionLocations(extractionJob?.locations),
    [extractionJob?.locations]
  );
  const hasReferences = Boolean(sortedReferences.length);
  const showReasoning = getHasAiReasoning({
    mode: question.mode,
    reasoning: extractionJob?.reasoning,
  });
  const isLocked = getIsJobLocked(extractionJob);

  return (
    <div className={classes.answerBox}>
      <div className={classes.answerHeader}>
        <Typography className={classes.answerText}>Answer</Typography>
        {isLocked && (
          <AlineTooltip arrow placement="top" title={getLockedByTooltip(extractionJob)}>
            <span className={classes.lockedAnswerIcon}>
              <Lock size={16} />
            </span>
          </AlineTooltip>
        )}
        <div className={classes.actionButtonsContainer}>
          <EditExtractionJobButton
            disabled={isLocked}
            handleEdit={() => setEditing(true)}
            isVisible={mouseOver}
          />
        </div>
      </div>
      <div className={classes.answerContainer}>
        <AiAnswerText
          documentId={documentId}
          editing={[editing, setEditing]}
          extractionJob={extractionJob}
          question={question}
        />
        {hasReferences && !editing && (
          <div className={classes.referencesRow}>
            {sortedReferences.map((reference, index) => (
              <ExtractionQuestionCitation
                key={index}
                documentId={documentId}
                questionId={question.id}
                reference={reference}
              />
            ))}
          </div>
        )}
      </div>
      {showReasoning && (
        <>
          <Divider sx={{ mt: 1 }} />
          <AiReasoningSection
            className={classes.reasoningSection}
            reasoning={extractionJob?.reasoning}
          />
        </>
      )}
    </div>
  );
}

export function EditExtractionJobButton({
  disabled,
  handleEdit,
  isVisible,
}: {
  disabled: boolean;
  handleEdit: (e: React.MouseEvent) => void;
  isVisible: boolean;
}): JSX.Element | null {
  return (
    <AlineTooltip arrow placement="top" title={disabled ? 'Answer locked' : 'Edit answer'}>
      <IconButton
        disabled={disabled}
        size="small"
        style={{ visibility: isVisible ? 'visible' : 'hidden' }}
        onClick={handleEdit}
      >
        <Pencil size={16} />
      </IconButton>
    </AlineTooltip>
  );
}

const useAnswerTextStyles = makeStyles()(theme => ({
  answerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  selectAnswerContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
}));

export interface AiAnswerJob extends Pick<
  Apollo.ExtractionJob,
  'id' | '__typename' | 'answer' | 'lockedAt'
> {
  lockedBy: Pick<Apollo.User, '__typename' | 'displayName' | 'id'> | null;
  selectedOptions: Pick<Apollo.QuestionOption, 'id' | '__typename' | 'label'>[];
}

export interface AiAnswerTextProps {
  documentId: string;
  editing: ReactSetStateTuple<boolean>;
  extractionJob: AiAnswerJob | null;
  question: Pick<Apollo.ExtractionQuestion, 'mode' | 'type' | 'id' | '__typename'>;
}

export function AiAnswerText({
  documentId,
  editing: [editing, setEditing],
  extractionJob,
  question,
}: AiAnswerTextProps): JSX.Element {
  const { classes } = useAnswerTextStyles();
  const isAiMode = question.mode === Apollo.ExtractionQuestionMode.Ai;

  if (editing) {
    return (
      <EditAiAnswerForm
        documentId={documentId}
        extractionJob={extractionJob}
        question={question}
        onEditClose={() => setEditing(false)}
      />
    );
  }

  switch (question.type) {
    case Apollo.ExtractionQuestionType.SingleSelect:
    case Apollo.ExtractionQuestionType.MultiSelect: {
      if (!extractionJob) {
        return (
          <Typography align="center">
            {isAiMode ? 'Run Aline AI' : 'Awaiting manual entry'}
          </Typography>
        );
      }

      if (!extractionJob.selectedOptions.length)
        return <Typography align="center">No answer found</Typography>;

      return (
        <div className={classes.selectAnswerContainer}>
          {extractionJob.selectedOptions.map(option => (
            <OptionItemChip key={option.id} option={option} />
          ))}
        </div>
      );
    }
    default: {
      if (!extractionJob) {
        return (
          <Typography align="center">
            {isAiMode ? 'Run Aline AI' : 'Awaiting manual entry'}
          </Typography>
        );
      }

      if (!extractionJob.answer) return <Typography align="center">No answer found</Typography>;

      const formattedAnswerText = getFormattedAnswerText({
        answerText: extractionJob.answer,
        type: question.type,
      });
      const lines = formattedAnswerText.split('\n');

      return (
        <div className={classes.answerContainer}>
          {lines.map((line, index) => (
            <Typography key={index}>{line || '\u200b'}</Typography>
          ))}
        </div>
      );
    }
  }
}

export function getFormattedAnswerText({
  answerText,
  type,
}: {
  answerText: string;
  type: Apollo.ExtractionQuestionType;
}): string {
  switch (type) {
    case Apollo.ExtractionQuestionType.Boolean:
      return answerText === 'true' ? 'Yes' : 'No';
    case Apollo.ExtractionQuestionType.Date: {
      const parsedDate = parseISO(answerText);
      if (!isValid(parsedDate)) return answerText;

      return format(parsedDate, 'MMM d, yyyy');
    }
    case Apollo.ExtractionQuestionType.Text:
    case Apollo.ExtractionQuestionType.Number:
      return answerText;
    case Apollo.ExtractionQuestionType.SingleSelect:
      return answerText || 'No selection';
    case Apollo.ExtractionQuestionType.MultiSelect: {
      return answerText || 'No selections';
    }
  }
}

export function useScrollToLocationForReference() {
  const editor = useMaybeEditor();
  const documentViewer = useMaybeDocumentViewer();
  const { clearSearchHighlights } = useClearSearchHighlights();
  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);
  const dispatchSearchErrorSnack = useDispatchNotification({
    message: 'There was an error searching for answer text.',
    variant: 'error',
  });
  const scrollToLocationForReference = useCallback(
    ({ aiReference, select }: { aiReference: SelectedReference; select?: 'end' }) => {
      switch (aiReference.__typename) {
        case 'FileLocation': {
          // Clear prior search results before re-running search.
          clearSearchHighlights();

          documentViewer?.setSearchHighlightColors({
            activeSearchResult: activeSearchHitColor.toString(),
            searchResult: inactiveSearchHitColor.toString(),
          });
          return documentViewer?.textSearchInit(
            /**
             * This is tricky, but referenceText can be an empty string, in which case we still have the PDF rect locations and page number
             * and we want to scroll to the reference.  But, Apryse WON'T search for an empty string.
             * Backfill this with a placeholder search so it always executes.
             */
            aiReference.referenceText || 'placeholder search',
            pdfTronCore.Search.Mode.HIGHLIGHT,
            {
              endPage: aiReference.pageNum,
              fullSearch: true,
              onDocumentEnd: async () => {
                const activeSearchHit = documentViewer.getActiveSearchResult();
                const quad = getFileReferenceQuad({
                  aiReference,
                  documentViewer,
                });
                if (!activeSearchHit) {
                  const annotationManager = documentViewer.getAnnotationManager();
                  const highlight = new pdfTronCore.Annotations.TextHighlightAnnotation({
                    PageNumber: aiReference.pageNum,
                    Quads: [quad],
                    StrokeColor: new pdfTronCore.Annotations.Color(
                      activeSearchHitColor.red(),
                      activeSearchHitColor.green(),
                      activeSearchHitColor.blue(),
                      activeSearchHitColor.alpha()
                    ),
                  });
                  highlight.setCustomData(PDFCustomDataKey.searchHighlight, String(true));

                  annotationManager.addAnnotation(highlight);
                  documentViewer.displayPageLocation(aiReference.pageNum, quad.x4, quad.y4, true);
                  await annotationManager.drawAnnotations({
                    majorRedraw: true,
                    pageNumber: aiReference.pageNum,
                  });
                }
              },
              onError: dispatchSearchErrorSnack,
              onResult: result => {
                const referenceQuad = getFileReferenceQuad({ aiReference, documentViewer });
                const parsedResult = SearchResultSchema.safeParse(result);
                if (!parsedResult.success) {
                  console.error(parsedResult.error);
                  Sentry.captureException(parsedResult.error);
                  return;
                }

                const firstQuad = parsedResult.data.quads.at(0);
                const {
                  x1 = 0,
                  x2 = 0,
                  x3 = 0,
                  x4 = 0,
                  y1 = 0,
                  y2 = 0,
                  y3 = 0,
                  y4 = 0,
                } = firstQuad?.getPoints() ?? {};
                const resultQuad = new pdfTronCore.Math.Quad(x1, y1, x2, y2, x3, y3, x4, y4);
                const referenceRectIntersectsResultRect = referenceQuad
                  .toRect()
                  .intersects(resultQuad.toRect());
                if (referenceRectIntersectsResultRect) {
                  documentViewer.displaySearchResult(result);
                }
              },
              startPage: aiReference.pageNum,
            }
          );
        }
        case 'AlineLocation': {
          if (!editor) return;

          const docHasPath = Node.has(editor, aiReference.locationPath);
          if (!docHasPath) return;

          const location = getCMLocationFromReference(editor, aiReference);
          const selectLocation = select === 'end' ? Editor.end(editor, location) : location;

          ClientTransforms.scrollToLocation(editor, {
            affinity: 'first',
            cMLocation: selectLocation,
            rWHeightPx,
            rWOffsetPx,
          });

          return ReactEditor.blur(editor);
        }
      }
    },
    [
      editor,
      rWHeightPx,
      rWOffsetPx,
      clearSearchHighlights,
      documentViewer,
      dispatchSearchErrorSnack,
    ]
  );

  return { scrollToLocationForReference };
}

interface MaybeRunningQuestion extends Pick<Apollo.ExtractionQuestion, 'id' | '__typename'> {
  extractionJob: PartialExtractionJob;
}

export function getIsExtractionQuestionRunning(
  document: DocumentWithAiQuestionIdsQueueing | null,
  question: MaybeRunningQuestion
): boolean {
  return (
    document?.aiQuestionIdsQueueing.includes(question.id) ||
    getIsExtractionJobRunning(question.extractionJob)
  );
}

type PartialExtractionJob = Partial<
  Pick<Apollo.ExtractionJob, 'failedAt' | 'finishedAt' | 'startedAt' | '__typename'>
> | null;

export function getIsExtractionJobRunning(job: PartialExtractionJob): boolean {
  return Boolean(job?.startedAt && !job.finishedAt && !job.failedAt);
}

export function getIsExtractionJobFailed(
  job: Partial<Pick<Apollo.ExtractionJob, 'failedAt' | '__typename'>> | null
): boolean {
  return Boolean(job?.failedAt);
}

export interface DocumentWithLatestVersion extends Pick<Apollo.Document, 'id' | '__typename'> {
  latestVersion: Pick<
    Apollo.FileVersion,
    'createdAt' | 'fileContentChangedAt' | 'id' | '__typename'
  >;
}
interface ExtractionJobWithVersion extends Partial<
  Pick<Apollo.ExtractionJob, 'finishedAt' | 'id' | 'questionContentHashAtRun' | '__typename'>
> {
  fileVersion?: Partial<Pick<Apollo.FileVersion, 'id' | '__typename'>> | null;
  question?: Partial<Pick<Apollo.ExtractionQuestion, 'contentHash' | '__typename'>> | null;
}

export function getIsExtractionJobStale(
  documentWithVersion: DocumentWithLatestVersion,
  job: ExtractionJobWithVersion | null
): boolean {
  const isFileVersionDifferent =
    !!job?.fileVersion && documentWithVersion.latestVersion.id !== job.fileVersion.id;
  const isContentHashDifferent = Boolean(
    job?.question?.contentHash &&
    (!job?.questionContentHashAtRun || job.question.contentHash !== job.questionContentHashAtRun)
  );

  if (!job?.finishedAt) return false;

  return (
    isFileVersionDifferent ||
    isContentHashDifferent ||
    isAfter(
      parseISO(
        documentWithVersion.latestVersion.fileContentChangedAt ??
          documentWithVersion.latestVersion.createdAt
      ),
      parseISO(job.finishedAt)
    )
  );
}

export function getIsCoreAiRunning(
  document: Pick<
    Apollo.Document,
    | 'coreAiPropertiesExtractionStartedAt'
    | 'coreAiPropertiesExtractionFailedAt'
    | 'coreAiPropertiesExtractionFinishedAt'
    | '__typename'
  > | null
): boolean {
  return Boolean(
    document?.coreAiPropertiesExtractionStartedAt &&
    !document.coreAiPropertiesExtractionFinishedAt &&
    !document.coreAiPropertiesExtractionFailedAt
  );
}
